import { Center, Group, Stack } from "@mantine/core"
import { showNotification } from "@mantine/notifications"
import { Button, Card, Loader } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import { CustomDrawer } from "~/components/CustomDrawer.tsx"
import {
  findCertificatesByOrganizationIdQueryKey,
  FindCertificatesByOrganizationIdResponseDto,
} from "~/gen/api"
import { CertificateForm } from "~/pages/Brand/Certificates/components/CertificateForm.tsx"
import { CertificateSummary } from "~/pages/Brand/Certificates/components/CertificateSummary.tsx"
import { useFindDocumentUrlQuery } from "~/pages/Brand/Certificates/hooks/useDocumentsQuery.ts"
import { useUpdateCertificateMutation } from "~/pages/Brand/Certificates/hooks/useUpdateCertificateMutation"

type Props = {
  certificate: FindCertificatesByOrganizationIdResponseDto
  onClose: () => void
  opened: boolean
}

export const CertificateVerificationModal = ({
  certificate,
  onClose,
  opened,
}: Props) => {
  const { t } = useTranslation("certificates")
  const { data: documentUrl, isLoading } = useFindDocumentUrlQuery(
    certificate.documentId,
    {
      enabled: !!certificate.documentId,
      refetchOnWindowFocus: false,
    }
  )
  const { mutateAsync: updateCertificate } = useUpdateCertificateMutation(
    certificate.id,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          findCertificatesByOrganizationIdQueryKey()
        )
        onClose()
        showNotification({
          message: t("modal.status.update.success"),
          color: "green",
        })
      },
      onError: () => {
        showNotification({
          message: t("modal.status.update.error"),
          color: "red",
        })
      },
    }
  )

  if (!certificate.documentId || !documentUrl) {
    return null
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <CustomDrawer size="80%" onClose={onClose} opened={opened}>
      <Group h="100%" p={0} gap={0} align="start">
        <Stack
          w="60%"
          h="100vh"
          p="md"
          bg="var(--mantine-color-brand-background)"
        >
          <iframe
            src={documentUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="Document Viewer"
          />
        </Stack>
        <Stack
          w="40%"
          bg="var(--mantine-color-brand-background)"
          h="100%"
          p="md"
          justify="start"
          align="start"
        >
          <Card w="100%">
            <Card.Header title={t("modal.title")} />
            <Card.Section>
              {certificate.status != "PENDING" ? (
                <Stack>
                  <CertificateSummary certificate={certificate} />
                  <Center>
                    <Button
                      w="max-content"
                      variant="secondary"
                      style={{ textTransform: "uppercase" }}
                      onClick={() => updateCertificate({ status: "PENDING" })}
                    >
                      {t("modal.edit")}
                    </Button>
                  </Center>
                </Stack>
              ) : (
                <CertificateForm certificate={certificate} onClose={onClose} />
              )}
            </Card.Section>
          </Card>
        </Stack>
      </Group>
    </CustomDrawer>
  )
}
